import { useEffect, useState, type SetStateAction } from "react";

export type PaginationCollectionKey = string | number | null;

/**
 * Client-side pagination for lists that have already been fetched in full.
 *
 * The list identity is the reset boundary: selecting a different student gives
 * us a new array, and page one is the only useful place to start.
 */
export function usePagination<T>(
  items: T[],
  pageSize: number,
  collectionKey?: PaginationCollectionKey,
) {
  // Keep legacy callers' array-identity reset behaviour until they opt into a
  // stable key for an equivalent refetch of the same collection.
  const identity: unknown = collectionKey === undefined ? items : collectionKey;
  const [state, setState] = useState({ identity, page: 1 });
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const sameCollection = Object.is(state.identity, identity);
  const currentPage = sameCollection ? Math.min(state.page, pages) : 1;

  useEffect(() => {
    setState((current) => {
      const page = Object.is(current.identity, identity)
        ? Math.min(current.page, pages)
        : 1;
      if (Object.is(current.identity, identity) && current.page === page) {
        return current;
      }
      return { identity, page };
    });
  }, [identity, pages]);

  function setPage(action: SetStateAction<number>) {
    setState((current) => {
      const currentPage = Object.is(current.identity, identity)
        ? current.page
        : 1;
      const requestedPage =
        typeof action === "function" ? action(currentPage) : action;
      const validPage = Number.isFinite(requestedPage)
        ? Math.trunc(requestedPage)
        : 1;
      const page = Math.max(1, Math.min(pages, validPage));
      return { identity, page };
    });
  }

  return {
    items: items.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    page: currentPage,
    pages,
    setPage,
    previous: () => setPage((current) => Math.max(1, current - 1)),
    next: () => setPage((current) => Math.min(pages, current + 1)),
  };
}

interface PaginationBaseProps {
  page: number;
  pages: number;
  disabled?: boolean;
  ariaLabel?: string;
  previousAriaLabel?: string;
  nextAriaLabel?: string;
}

type PaginationProps = PaginationBaseProps &
  (
    | {
        /** Server/external paging: the caller owns the requested page. */
        onPageChange: (page: number) => void;
      }
    | {
        /** Client paging: use the callbacks returned by usePagination. */
        onPrevious: () => void;
        onNext: () => void;
      }
  );

/**
 * Shared previous/next controls for both client-side slices and server pages.
 * The caller always owns paging state; this component only renders navigation.
 */
export function Pagination(props: PaginationProps) {
  const { page, pages, disabled = false, ariaLabel = "Pagination" } = props;
  if (pages <= 1) return null;

  const previous =
    "onPageChange" in props ? () => props.onPageChange(page - 1) : props.onPrevious;
  const next = "onPageChange" in props ? () => props.onPageChange(page + 1) : props.onNext;

  return (
    <nav className="pagination" aria-label={ariaLabel}>
      <button
        className="action-tertiary small"
        type="button"
        aria-label={props.previousAriaLabel ?? "Previous page"}
        disabled={disabled || page <= 1}
        onClick={previous}
      >
        Previous
      </button>
      <span className="pagination-status muted small" aria-current="page">
        Page {page} of {pages}
      </span>
      <button
        className="action-tertiary small"
        type="button"
        aria-label={props.nextAriaLabel ?? "Next page"}
        disabled={disabled || page >= pages}
        onClick={next}
      >
        Next
      </button>
    </nav>
  );
}
